// VENDORED from extensions/word-certifier/src/hash.ts — a byte-for-byte copy of
// a frozen cross-repo contract, not a fork. simhash64-v1 has four other
// implementations (coc Go internal/fuzzy, the Word add-in, the gdoc extension,
// infrastructure's Python verifier). Do NOT edit the algorithm here; a change is
// a new algorithm id, coordinated across all five.

// Content fingerprint — SimHash (Charikar) over normalized word 3-shingles,
// with a SHA-256 feature hash ("simhash64-v1").
//
// VERSIONED CONTRACT. The Word add-in, the GDoc extension, infrastructure's
// Python verifier, and the chain-of-creation Go reference verifier MUST all produce the
// same fingerprint for the same text, so the SAME work seen as a Google Doc, a
// .docx, or an uploaded copy matches by Hamming closeness WITHOUT transmitting
// any text. The feature hash is SHA-256 (one definition in every language) so a
// third party reproduces the digest with only their standard library. Do not
// "improve" normalize()/shingles()/the feature hash — that would silently break
// cross-format matching and requires a new algorithm id. Parity is pinned by the
// shared golden vectors in the chain-of-creation repo
// (spec/simhash64-vectors.json), asserted here in test/hash.test.ts.

const VERSION = 1;
const SHINGLE = 3; // word k-shingle size

export interface Fingerprint {
  algo: "simhash64";
  version: number;
  shingle: number;
  tokens: number;
  hash: string; // 16 hex chars, high 32 bits first
}

const utf8 = new TextEncoder();

// Synchronous SHA-256 (bytes → 32 bytes). Self-contained so the fingerprint stays
// synchronous and identical across the add-in, the (bundler-free) extension, and
// the app. Standard FIPS 180-4; matches crypto.subtle / Go crypto/sha256 exactly.
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function sha256(bytes: Uint8Array): Uint8Array {
  const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));
  let h0 = 0x6a09e667,
    h1 = 0xbb67ae85,
    h2 = 0x3c6ef372,
    h3 = 0xa54ff53a,
    h4 = 0x510e527f,
    h5 = 0x9b05688c,
    h6 = 0x1f83d9ab,
    h7 = 0x5be0cd19;
  const l = bytes.length;
  const k = (56 - ((l + 1) % 64) + 64) % 64;
  const total = l + 1 + k + 8;
  const m = new Uint8Array(total);
  m.set(bytes);
  m[l] = 0x80;
  const dv = new DataView(m.buffer);
  const bitLen = l * 8;
  dv.setUint32(total - 4, bitLen >>> 0);
  dv.setUint32(total - 8, Math.floor(bitLen / 0x100000000) >>> 0);
  const w = new Uint32Array(64);
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = h0,
      b = h1,
      c = h2,
      d = h3,
      e = h4,
      f = h5,
      g = h6,
      h = h7;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + SHA256_K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }
  const out = new Uint8Array(32);
  const odv = new DataView(out.buffer);
  [h0, h1, h2, h3, h4, h5, h6, h7].forEach((x, i) => odv.setUint32(i * 4, x));
  return out;
}

// Normalize away cross-format noise (the step that makes formats agree).
function normalize(text: string): string {
  return text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[‘’‚‛]/g, "'") // curly single quotes → '
    .replace(/[“”„‟]/g, '"') // curly double quotes → "
    .replace(/[–—―]/g, "-") // en/em/horizontal dash → -
    .replace(/[^\p{L}\p{N}]+/gu, " ") // any non-alphanumeric run → single space
    .trim();
}

function shingles(tokens: string[]): string[] {
  if (tokens.length < SHINGLE) return tokens.length ? [tokens.join(" ")] : [];
  const out: string[] = [];
  for (let i = 0; i + SHINGLE <= tokens.length; i++) {
    out.push(tokens.slice(i, i + SHINGLE).join(" "));
  }
  return out;
}

const POPCOUNT16 = (() => {
  const t = new Uint8Array(65536);
  for (let i = 0; i < 65536; i++) t[i] = (i & 1) + t[i >> 1];
  return t;
})();
const popcount32 = (x: number): number => POPCOUNT16[x & 0xffff] + POPCOUNT16[(x >>> 16) & 0xffff];
const hex8 = (x: number): string => (x >>> 0).toString(16).padStart(8, "0");

// Compute the fingerprint. Returns null for empty / sub-shingle text so callers
// can simply omit it rather than send a meaningless hash. Omitting is conforming:
// verification-spec §6.3 makes the no-shingle case implementation-defined (emit
// the all-zero digest or omit the field), and a verifier must read a missing
// fingerprint as "no claim". See 86bbafv1d.
export function simhash64(text: string): Fingerprint | null {
  const tokens = normalize(text || "").split(" ").filter(Boolean);
  const feats = shingles(tokens);
  if (!feats.length) return null;

  const counts = new Int32Array(64);
  for (const f of feats) {
    // feature = big-endian uint64 of SHA-256(shingle)[0:8]; hi = bytes 0..3, lo = 4..7.
    const d = sha256(utf8.encode(f));
    const hi = ((d[0] << 24) | (d[1] << 16) | (d[2] << 8) | d[3]) >>> 0; // bits 32..63
    const lo = ((d[4] << 24) | (d[5] << 16) | (d[6] << 8) | d[7]) >>> 0; // bits 0..31
    for (let b = 0; b < 32; b++) counts[b] += lo & (1 << b) ? 1 : -1;
    for (let b = 0; b < 32; b++) counts[b + 32] += hi & (1 << b) ? 1 : -1;
  }

  let lo = 0;
  let hi = 0;
  for (let b = 0; b < 32; b++) if (counts[b] > 0) lo |= 1 << b;
  for (let b = 0; b < 32; b++) if (counts[b + 32] > 0) hi |= 1 << b;

  return {
    algo: "simhash64",
    version: VERSION,
    shingle: SHINGLE,
    tokens: tokens.length,
    hash: hex8(hi) + hex8(lo), // 16 hex chars, high 32 bits first
  };
}

// Hamming distance between two 16-hex-char fingerprints (0..64; lower = closer).
export function hammingHex(a: string, b: string): number {
  const aHi = parseInt(a.slice(0, 8), 16);
  const aLo = parseInt(a.slice(8), 16);
  const bHi = parseInt(b.slice(0, 8), 16);
  const bLo = parseInt(b.slice(8), 16);
  return popcount32(aHi ^ bHi) + popcount32(aLo ^ bLo);
}

// Normalized closeness in [0,1] (1 = identical).
export function similarity(a: string, b: string): number {
  return 1 - hammingHex(a, b) / 64;
}

// Canonical normalized token stream shared by the fuzzy fingerprint and the
// exact hash: normalize() then single-space-join the word tokens. Reproducible
// by any party that applies normalize() — no format-specific bytes.
export function canonicalText(text: string): string {
  return normalize(text || "").split(" ").filter(Boolean).join(" ");
}

export { normalize };
